/**
 * Supported target locales for book translation (v1).
 */

export type TranslationLocaleCode = "es-419" | "es-ES" | "pt-BR" | "pt-PT" | "fr";

export type TranslationLocale = {
  code: TranslationLocaleCode;
  /** Version name default, e.g. "Español (Latinoamérica)" */
  label: string;
  /** Short English label for UI */
  englishLabel: string;
  /** Prompt hints for register / regional vocabulary */
  promptHint: string;
};

export const TRANSLATION_LOCALES: readonly TranslationLocale[] = [
  {
    code: "es-419",
    label: "Español (Latinoamérica)",
    englishLabel: "Spanish (Latin America)",
    promptHint:
      "Use Latin American Spanish (neutral/general LatAm). Prefer ustedes over vosotros, avoid Spain-specific slang (e.g. prefer computadora/celular over ordenador/móvil where variants differ).",
  },
  {
    code: "es-ES",
    label: "Español (España)",
    englishLabel: "Spanish (Spain)",
    promptHint:
      "Use European Spanish (Spain). Prefer vosotros where natural for informal plural, and Spain vocabulary (ordenador, móvil, etc.) when variants differ from Latin America.",
  },
  {
    code: "pt-BR",
    label: "Português (Brasil)",
    englishLabel: "Portuguese (Brazil)",
    promptHint:
      "Use Brazilian Portuguese. Prefer você/vocês forms typical of Brazil, Brazilian spelling and vocabulary (e.g. ônibus, trem).",
  },
  {
    code: "pt-PT",
    label: "Português (Portugal)",
    englishLabel: "Portuguese (Portugal)",
    promptHint:
      "Use European Portuguese (Portugal). Prefer European spelling and vocabulary (e.g. autocarro, comboio) and register appropriate to literary fiction in Portugal.",
  },
  {
    code: "fr",
    label: "Français",
    englishLabel: "French",
    promptHint:
      "Use standard literary French (France). Prefer natural literary register over stiff literal calques; keep dialogue natural.",
  },
] as const;

export function isTranslationLocaleCode(value: unknown): value is TranslationLocaleCode {
  return (
    value === "es-419" ||
    value === "es-ES" ||
    value === "pt-BR" ||
    value === "pt-PT" ||
    value === "fr"
  );
}

export function getTranslationLocale(code: string): TranslationLocale | null {
  return TRANSLATION_LOCALES.find((locale) => locale.code === code) ?? null;
}

export function defaultVersionNameForLocale(code: TranslationLocaleCode): string {
  return getTranslationLocale(code)?.label ?? code;
}

/**
 * Stored on BookVersion.bookConfig.translation.
 * Promote-to-Book later should copy targetLocale → BookPublishingMetadata.language.
 */
export type VersionTranslationConfig = {
  targetLocale: TranslationLocaleCode;
  sourceLocale: string;
  /** ISO timestamp when batch translation finished (may be partial). */
  completedAt?: string;
  /** Snapshot of protected term surfaces used for this run. */
  protectedTermSurfaces?: string[];
  /** Reserved for promote-to-Book: suggested publishing language = targetLocale. */
  // TODO(promote-to-book): copy targetLocale to new BookPublishingMetadata.language
  promoteToBook?: {
    suggestedLanguage: TranslationLocaleCode;
  };
};

export function buildVersionTranslationConfig(params: {
  targetLocale: TranslationLocaleCode;
  sourceLocale?: string;
  protectedTermSurfaces?: string[];
  completedAt?: string;
}): VersionTranslationConfig {
  return {
    targetLocale: params.targetLocale,
    sourceLocale: params.sourceLocale?.trim() || "en",
    ...(params.protectedTermSurfaces?.length
      ? { protectedTermSurfaces: [...params.protectedTermSurfaces] }
      : {}),
    ...(params.completedAt ? { completedAt: params.completedAt } : {}),
    promoteToBook: { suggestedLanguage: params.targetLocale },
  };
}
